import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["item", "badge"]
  static values = { url: String, recalculateUrl: String }

  connect() {
    this.draggedItem = null
    this.itemTargets.forEach((item) => {
      item.addEventListener("dragstart", this.onDragStart)
      item.addEventListener("dragover", this.onDragOver)
      item.addEventListener("drop", this.onDrop)
      item.addEventListener("dragend", this.onDragEnd)
    })
  }

  disconnect() {
    this.itemTargets.forEach((item) => {
      item.removeEventListener("dragstart", this.onDragStart)
      item.removeEventListener("dragover", this.onDragOver)
      item.removeEventListener("drop", this.onDrop)
      item.removeEventListener("dragend", this.onDragEnd)
    })
  }

  onDragStart = (event) => {
    this.draggedItem = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
    event.currentTarget.classList.add("opacity-60")
  }

  onDragOver = (event) => {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.draggedItem || target === this.draggedItem) return

    const rect = target.getBoundingClientRect()
    const isAfter = event.clientY > rect.top + rect.height / 2

    if (isAfter) {
      target.after(this.draggedItem)
    } else {
      target.before(this.draggedItem)
    }
  }

  onDrop = (event) => {
    event.preventDefault()
    this.updateBadges()
    this.persistOrder()
  }

  onDragEnd = () => {
    if (this.draggedItem) {
      this.draggedItem.classList.remove("opacity-60")
    }
    this.draggedItem = null
  }

  moveUp(event) {
    const item = event.currentTarget.closest("[data-poi-id]")
    if (!item) return
    const previous = item.previousElementSibling
    if (!previous) return
    previous.before(item)
    this.updateBadges()
    this.persistOrder()
  }

  moveDown(event) {
    const item = event.currentTarget.closest("[data-poi-id]")
    if (!item) return
    const next = item.nextElementSibling
    if (!next) return
    next.after(item)
    this.updateBadges()
    this.persistOrder()
  }

  updateBadges() {
    const items = this.itemTargets
    items.forEach((item, index) => {
      const badge = item.querySelector("[data-poi-reorder-target='badge']")
      if (!badge) return
      badge.textContent = index === 0 ? "D" : String(index)
    })
  }

  async persistOrder() {
    if (!this.hasUrlValue) return

    const ids = Array.from(this.element.querySelectorAll("[data-poi-id]"))
      .map((item) => item.dataset.poiId)
    const token = document.querySelector("meta[name='csrf-token']")?.content

    const response = await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": token
      },
      credentials: "same-origin",
      body: JSON.stringify({ poi_ids: ids })
    })

    if (!response.ok) {
      console.warn("[poi-reorder] failed to persist order", response.status)
      return
    }

    if (!this.hasRecalculateUrlValue) return
    const recalcResponse = await fetch(this.recalculateUrlValue, {
      method: "POST",
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token
      },
      credentials: "same-origin"
    })
    if (!recalcResponse.ok) {
      console.warn("[poi-reorder] failed to recalculate route", recalcResponse.status)
      return
    }
    const stream = await recalcResponse.text()
    if (window.Turbo && typeof window.Turbo.renderStreamMessage === "function") {
      window.Turbo.renderStreamMessage(stream)
    }
  }
}
